import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { parseInt } from 'lodash';
import { FormControl, Select, MenuItem } from '@mui/material';
import MuiSelectUpDown from './MuiSelectUpDown';
import SkeletonWrapper from './skeleton/SkeletonWrapper';

/**
 * @typedef {object} MultiItemUpDownOwnProps
 * @property {string} [selectId]
 * @property {(selectedKey: string | number) => void} setValue
 * @property {Array<*>} [options]
 * @property {string} [upDownId]
 * @property {boolean} [preventUpDownBtnTabFocus]
 * @property {boolean} [loading]
 */

/**
 * @typedef {MultiItemUpDownOwnProps & React.ComponentProps<typeof Select>} MultiItemUpDownProps
 */

/**
 * @param {MultiItemUpDownProps} props
 */
const MultiItemUpDown = props => {
  const {
    value,
    setValue,
    variant,
    selectId,
    upDownId,
    preventUpDownBtnTabFocus,
    disabled,
    options = [],
    loading = false,
    ...otherProps
  } = props;

  const [disableDownArrow, setDisableDownArrow] = useState(false);
  const [disableUpArrow, setDisableUpArrow] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const min = 0;
  const max = options.length - 1;

  const classes = cx('mui-input-number__choice-adornment', 'mui-input-number__hover-pointer', {
    'mui-input-number__adornment--outlined': variant === 'outlined'
  });

  const Adornment = (
    <MuiSelectUpDown
      className={classes}
      disableDownArrow={disabled || disableDownArrow}
      disableUpArrow={disabled || disableUpArrow}
      onClickDown={() => onValueChange(selectedIndex + 1)}
      onClickUp={() => onValueChange(selectedIndex - 1)}
      upDownId={upDownId}
      preventUpDownBtnTabFocus={preventUpDownBtnTabFocus}
    />
  );

  useEffect(() => {
    if (value) {
      setSelectedIndex(options.findIndex(x => x.key === value));
    }
  }, [options, value]);

  useEffect(() => {
    if (selectedIndex != null) {
      setDisableDownArrow(selectedIndex === max);
      setDisableUpArrow(selectedIndex === min);
    }
  }, [selectedIndex, min, max]);

  const onValueChange = value => {
    if (value === '' || !options.length) {
      return;
    }
    const newValue = parseInt(value);
    setSelectedIndex(newValue);
    setValue(options[newValue].key);
  };

  const setValues = val => {
    const index = options.findIndex(x => x.key === val);
    if (index >= 0) {
      setSelectedIndex(index);
    }
    setValue(val);
  };

  const handleFocus = () => {
    if (focused) {
      return;
    }

    setFocused(true);
    setOpen(true);
  };

  const wrapperClasses = cx('mui-input-number mui-input-number__choice-wrapper', {
    'mui-input-number__wrapper--outlined': variant === 'outlined'
  });

  return (
    <div className={wrapperClasses}>
      <SkeletonWrapper show={loading} inheritParentSize={true}>
        <FormControl variant='standard'>
          <Select
            {...otherProps}
            variant='standard'
            value={options.length ? value : ''}
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            onFocus={handleFocus}
            onBlur={() => setFocused(false)}
            onChange={e => setValues(e.target.value)}
            displayEmpty
            disableUnderline
            disabled={disabled}
            inputProps={{
              id: selectId,
              'data-testid': `${selectId}-select`
            }}
            IconComponent={() => null}>
            {options.map((option, i) => (
              <MenuItem
                value={option.key}
                key={i}
                className='mui-focus-visible'
                onKeyDown={e => {
                  if (e.keyCode === 32 || e.key === ' ') {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {Adornment}
      </SkeletonWrapper>
    </div>
  );
};

export default MultiItemUpDown;
